import { useCallback, useState } from 'react';

interface ActionButtonsProps {
  /** Whether the item is favorited */
  isFavorited: boolean;
  /** Whether a favorite mutation is pending */
  isPending: boolean;
  /** Called when favorite button is clicked */
  onToggleFavorite: () => void;
  /** URL to share */
  shareUrl: string;
  /** Title for sharing */
  shareTitle: string;
  /** Whether to show buttons (only when authenticated for favorite) */
  showFavorite: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
}

/**
 * Reusable action buttons for favorite and share functionality.
 * Used on topic cards and episode cards on the homepage.
 */
export function ActionButtons({
  isFavorited,
  isPending,
  onToggleFavorite,
  shareUrl,
  shareTitle,
  showFavorite,
  size = 'md',
}: ActionButtonsProps) {
  const [showCopied, setShowCopied] = useState(false);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        return;
      } catch {
        // User cancelled or share failed, fall through to clipboard
      }
    }

    // Fall back to clipboard copy
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch {
      // Clipboard failed silently
    }
  }, [shareUrl, shareTitle]);

  const handleFavorite = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite();
  }, [onToggleFavorite]);

  const buttonSize = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <div className="flex items-center gap-1.5">
      {/* Favorite button - only shown when authenticated */}
      {showFavorite && (
        <button
          type="button"
          onClick={handleFavorite}
          disabled={isPending}
          className={`flex ${buttonSize} items-center justify-center rounded-full transition-all ${
            isFavorited
              ? 'bg-pink-500/20 text-pink-400 hover:bg-pink-500/30'
              : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 hover:text-pink-400'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isPending ? (
            <svg className={`${iconSize} animate-spin`} viewBox="0 0 24 24" aria-hidden="true">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className={iconSize}
              viewBox="0 0 24 24"
              fill={isFavorited ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
              />
            </svg>
          )}
        </button>
      )}

      {/* Share button */}
      <div className="relative">
        <button
          type="button"
          onClick={handleShare}
          className={`flex ${buttonSize} items-center justify-center rounded-full bg-gray-700/50 text-gray-400 transition-all hover:bg-gray-700 hover:text-white`}
          title="Share"
          aria-label="Share"
        >
          <svg
            className={iconSize}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935-2.186 2.25 2.25 0 0 0-3.935 2.186zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185z"
            />
          </svg>
        </button>

        {/* Copied tooltip */}
        {showCopied && (
          <div className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
            Copied!
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>
        )}
      </div>
    </div>
  );
}
